import { collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, query, where, orderBy, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { genericEventService } from './generic-event.service';

export interface GenericTask {
  id?: string;
  eventId: string;
  eventType: string;
  title: string;
  description?: string;
  assignedTo?: string[];
  category: string;
  priority: 'low' | 'medium' | 'high';
  status: 'todo' | 'in_progress' | 'completed';
  dueDate?: Date | null;
  createdAt: Date;
  updatedAt: Date;
  completedAt?: Date;
}

export class GenericTasksService {
  
  // Créer une tâche
  async createTask(eventId: string, eventType: string, taskData: Omit<Partial<GenericTask>, 'status'> & { status?: GenericTask['status'] | 'done' }): Promise<string> {
    const collections = genericEventService.getCollectionNames(eventType);
    
    try {
      // VÉRIFICATION DES DOUBLONS AVANT CRÉATION
      if (taskData.title && taskData.category) {
        const existingTasks = await this.getEventTasks(eventId, eventType);
        const duplicateTask = existingTasks.find(task => 
          task.title === taskData.title && 
          task.category === taskData.category
        );

        if (duplicateTask) {
          return duplicateTask.id!; // Retourner l'ID de la tâche existante
        }
      }

      const taskRef = doc(collection(db, collections.tasks));

      const task: GenericTask = {
        id: taskRef.id,
        eventId,
        eventType,
        title: taskData.title || 'Nouvelle tâche',
        description: taskData.description || '',
        assignedTo: taskData.assignedTo || [],
        category: taskData.category || 'general',
        priority: taskData.priority || 'medium',
        status: taskData.status === 'done' ? 'completed' : taskData.status || 'todo',
        dueDate: taskData.dueDate || null,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const taskToSave = {
        ...task,
        dueDate: task.dueDate ? Timestamp.fromDate(task.dueDate) : null,
        createdAt: Timestamp.fromDate(task.createdAt),
        updatedAt: Timestamp.fromDate(task.updatedAt)
      };

      await setDoc(taskRef, taskToSave);
      const savedTask = await getDoc(taskRef);
      if (savedTask.exists()) {} else {
        console.error(`❌ [GenericTasksService] Task verification failed - task not found in database`);
      }

      return taskRef.id;
    } catch (error) {
      console.error('[DEBUG] Erreur lors de la création de la tâche:', error);
      console.error('[DEBUG] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        eventId,
        eventType,
        taskData,
        collections
      });
      throw error;
    }
  }

  // Récupérer toutes les tâches d'un événement
  async getEventTasks(eventId: string, eventType: string): Promise<GenericTask[]> {
    const collections = genericEventService.getCollectionNames(eventType);
    
    try {
      const tasksRef = collection(db, collections.tasks);
      const q = query(
        tasksRef, 
        where('eventId', '==', eventId)
      );

      const querySnapshot = await getDocs(q);

      const tasks = querySnapshot.docs.map((doc, index) => {
        const data = doc.data();

        return {
          ...data,
          id: doc.id,
          dueDate: data['dueDate']?.toDate() || null,
          createdAt: data['createdAt']?.toDate() || new Date(),
          updatedAt: data['updatedAt']?.toDate() || new Date(),
          completedAt: data['completedAt']?.toDate() || null
        } as GenericTask;
      });

      // Tri en mémoire pour éviter d'avoir besoin d'un index composite pour chaque type d'événement
      tasks.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      return tasks;
    } catch (error) {
      console.error('[DEBUG] Erreur lors de la récupération des tâches:', error);
      console.error('[DEBUG] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        eventId,
        eventType,
        collections
      });
      return [];
    }
  }

  // Mettre à jour une tâche
  async updateTask(eventId: string, eventType: string, taskId: string, updates: Omit<Partial<GenericTask>, 'status'> & { status?: GenericTask['status'] | 'done' }): Promise<void> {
    const collections = genericEventService.getCollectionNames(eventType);
    
    try {
      // Vérifier que la tâche appartient bien à l'événement
      const taskRef = doc(db, collections.tasks, taskId);
      const taskDoc = await getDoc(taskRef);
      
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      const taskData = taskDoc.data();
      if (taskData.eventId !== eventId) {
        throw new Error('Task does not belong to this event');
      }
      
      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.fromDate(new Date())
      };

      if (updates.dueDate) {
        updateData.dueDate = Timestamp.fromDate(updates.dueDate);
      }

      if ((updates.status === 'done' || updates.status === 'completed') && !updates.completedAt) {
        updateData.completedAt = Timestamp.fromDate(new Date());
        updateData.status = 'completed'; // Assurer la compatibilité des statuts
      } else if (updates.status === 'done') {
        updateData.status = 'completed';
      }

      await updateDoc(taskRef, updateData);
    } catch (error) {
      console.error('[DEBUG] Erreur lors de la mise à jour de la tâche:', error);
      throw error;
    }
  }

  // Supprimer une tâche
  async deleteTask(eventId: string, eventType: string, taskId: string): Promise<void> {
    const collections = genericEventService.getCollectionNames(eventType);
    
    try {
      // Vérifier que la tâche appartient bien à l'événement
      const taskRef = doc(db, collections.tasks, taskId);
      const taskDoc = await getDoc(taskRef);
      
      if (taskDoc.exists()) {
        const taskData = taskDoc.data();
        if (taskData.eventId !== eventId) {
          throw new Error('Task does not belong to the selected event');
        }
      }
      
      await deleteDoc(taskRef);
    } catch (error) {
      console.error('[DEBUG] Erreur lors de la suppression de la tâche:', error);
      throw error;
    }
  }

  // Obtenir les statistiques des tâches
  async getTaskStats(eventId: string, eventType: string): Promise<{
    total: number;
    completed: number;
    inProgress: number;
    todo: number;
    overdue: number;
  }> {
    const tasks = await this.getEventTasks(eventId, eventType);
    const now = new Date();

    return {
      total: tasks.length,
      completed: tasks.filter(t => t.status === 'completed').length,
      inProgress: tasks.filter(t => t.status === 'in_progress').length,
      todo: tasks.filter(t => t.status === 'todo').length,
      overdue: tasks.filter(t => 
        t.status !== 'completed' && 
        t.dueDate && 
        t.dueDate < now
      ).length
    };
  }

  // Nettoyer les tâches dupliquées d'un événement
  async cleanDuplicateTasks(eventId: string, eventType: string): Promise<{
    totalTasks: number;
    duplicatesRemoved: number;
    remainingTasks: number;
  }> {
    try {
      const allTasks = await this.getEventTasks(eventId, eventType);

      if (allTasks.length === 0) {
        return { totalTasks: 0, duplicatesRemoved: 0, remainingTasks: 0 };
      }

      // Grouper les tâches par titre et catégorie pour identifier les doublons
      const taskGroups = new Map<string, GenericTask[]>();

      allTasks.forEach(task => {
        const key = `${task.title}|${task.category}`;
        if (!taskGroups.has(key)) {
          taskGroups.set(key, []);
        }
        taskGroups.get(key)!.push(task);
      });

      let duplicatesRemoved = 0;
      const tasksToKeep: GenericTask[] = [];

      // Pour chaque groupe, garder seulement la tâche la plus récente
      for (const [key, tasks] of taskGroups) {
        if (tasks.length > 1) {
          // Trier par date de création (plus récent en premier)
          tasks.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

          // Garder le premier (plus récent), supprimer les autres
          const taskToKeep = tasks[0];
          if (taskToKeep) {
            tasksToKeep.push(taskToKeep);
          }

          // Supprimer les doublons
          for (let i = 1; i < tasks.length; i++) {
            const taskToDelete = tasks[i];
            if (taskToDelete && taskToDelete.id) {
              try {
                await this.deleteTask(eventId, eventType, taskToDelete.id!);
                duplicatesRemoved++;
              } catch (error) {
                console.error(`❌ [GenericTasksService] Failed to delete duplicate task ${taskToDelete.id}:`, error);
              }
            }
          }
        } else {
          // Pas de doublons, garder la tâche
          const taskToKeep = tasks[0];
          if (taskToKeep) {
            tasksToKeep.push(taskToKeep);
          }
        }
      }

      return { totalTasks: allTasks.length, duplicatesRemoved, remainingTasks: allTasks.length - duplicatesRemoved };
    } catch (error) {
      console.error('[DEBUG] Erreur lors du nettoyage des tâches:', error);
      throw error;
    }
  }

  // Auto-compléter certaines tâches en fonction des mises à jour de l'événement
  async autoCompleteTasks(eventId: string, eventType: string, updates: any): Promise<void> {
    try {
      const tasks = await this.getEventTasks(eventId, eventType);
      
      const tasksToComplete: string[] = [];

      // Si la date est mise à jour et n'est pas nulle
      if (updates.date !== undefined && updates.date !== null) {
        // Chercher les tâches liées à la date (ex: "Choisir la date du mariage", "Fixer la date")
        const dateTasks = tasks.filter(t => 
          t.title.toLowerCase().includes('date') && 
          t.status !== 'completed'
        );
        dateTasks.forEach(t => {
          if (t.id && !tasksToComplete.includes(t.id)) tasksToComplete.push(t.id);
        });
      }

      // Si le lieu est mis à jour et n'est pas vide
      if (updates.location !== undefined && updates.location !== null && updates.location !== '') {
        // Chercher les tâches liées au lieu (ex: "Rechercher le lieu de réception", "Réserver le lieu")
        const venueTasks = tasks.filter(t => 
          (t.title.toLowerCase().includes('lieu') || t.title.toLowerCase().includes('endroit')) && 
          t.status !== 'completed'
        );
        venueTasks.forEach(t => {
          if (t.id && !tasksToComplete.includes(t.id)) tasksToComplete.push(t.id);
        });
      }

      // Mettre à jour les tâches trouvées
      for (const taskId of tasksToComplete) {
        await this.updateTask(eventId, eventType, taskId, {
          status: 'completed',
          completedAt: new Date()
        });
      }
    } catch (error) {
      console.error('[DEBUG] Erreur lors de l\'auto-complétion des tâches:', error);
    }
  }

  // Auto-compléter des tâches spécifiques en fonction de mots-clés (ex: ajout d'un invité -> "Établir la liste des invités")
  async completeTasksByKeywords(eventId: string, eventType: string, keywords: string[]): Promise<void> {
    try {
      const tasks = await this.getEventTasks(eventId, eventType);
      
      const tasksToComplete = tasks.filter(t => 
        t.status !== 'completed' && 
        keywords.some(kw => t.title.toLowerCase().includes(kw.toLowerCase()))
      );

      for (const t of tasksToComplete) {
        if (t.id) {
          await this.updateTask(eventId, eventType, t.id, {
            status: 'completed',
            completedAt: new Date()
          });
        }
      }
    } catch (error) {
      console.error('[DEBUG] Erreur lors de l\'auto-complétion par mots-clés:', error);
    }
  }
}

export const genericTasksService = new GenericTasksService();